import React, { useCallback, useMemo, useState } from "react";
import { Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/react";
import { Formik } from "formik";
import { Flex, Grid, Heading, Text } from "@chakra-ui/react";
import AppListHeader from "@/features/ui/app-list-header";
import AppButton from "@/features/line-sheet-sets/app-button";
import useIdentity from "@/features/ui/hooks/use-identity";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { Buyer, Company, isFile, isStoredObject } from "@/features/types";
import { ProformaInvoice } from "@/features/invoices/proforma-invoices/proforma-invoice.type";
import { useTransportationDumbTransaction } from "@/features/transportations/hooks/use-transportation-dumb-transaction";
import TransportationForm from "@/features/transportations/transportation-form";
import { TransportationFormFields } from "@/features/transportations/upload-transportation-page";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useListShippingInfos from "@/features/my-account/hooks/use-list-shipping-infos";
import useCreateTransportationPayment from "@/features/transportations/hooks/use-create-transportation-payment";
import useStoreObject from "@/features/ui/hooks/use-store-object";
import {
  PaymentFormState,
  TransportationPaymentInlineForm,
} from "@/features/transportations/transportation-payment-form-control";
import { DateUtils } from "@/features/ui/utils/date-utils";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  shippedTo?: Buyer;
  shippedFromList?: Company[];
  proformaInvoices?: Pick<
    ProformaInvoice,
    "id" | "number" | "issuedByCompany" | "issuedToCompany"
  >[];
  onSuccess?: () => void;
}

const BASE_MODAL_HIDDEN_FIELDS: (keyof TransportationFormFields)[] = [
  "shippedFromList",
  "shippedTo",
  "flightNumber",
  "flightDepartureAirport",
  "flightArrivalAirport",
  "flightEtd",
  "flightAtd",
  "flightAta",
  "finalEta",
  "cost",
  "forwarderInvoiceNumbers",
];

export default function AddTransportationModal({
  isOpen,
  onClose,
  shippedTo,
  shippedFromList,
  proformaInvoices,
  onSuccess,
}: Props) {
  const identity = useIdentity();
  const { tTitle } = useI18nHelper();
  const { warning: showWarning, error: showError } = useAppToasts();
  const { addCreate } = useTransportationDumbTransaction();
  const { fire: fireCreatePayment } = useCreateTransportationPayment();
  const { fire: fireStoreObject } = useStoreObject();

  const [paymentForm, setPaymentForm] = useState<PaymentFormState>({
    issuedOn: new Date(),
    amount: "",
    isRemitted: false,
    remittedOn: null,
    file: null,
  });

  const handleClose = useCallback(() => {
    setPaymentForm({
      issuedOn: new Date(),
      amount: "",
      isRemitted: false,
      remittedOn: null,
      file: null,
    });
    onClose();
  }, [onClose]);

  const { data: shippingInfos } = useListShippingInfos(
    isOpen && shippedTo ? { companyId: shippedTo.id } : undefined,
    { revalidateOnFocus: false, dedupingInterval: 0 }
  );

  const defaultShippingInfo = useMemo(() => {
    if (!shippingInfos || shippingInfos.length === 0) return undefined;
    return shippingInfos.find((info) => info.isDefault) ?? shippingInfos[0];
  }, [shippingInfos]);

  const validate = useCallback(
    (values: TransportationFormFields) => {
      const errors: Partial<Record<keyof TransportationFormFields, string>> =
        {};
      if (values.proformaInvoices.length === 0) {
        errors.proformaInvoices = "required";
        showWarning(tTitle("proforma_invoices") + " 연결이 필요합니다.");
      }
      return errors;
    },
    [showWarning, tTitle]
  );

  const handleSubmit = useCallback(
    async (values: TransportationFormFields) => {
      if (identity?.company?.type !== "AGENCY") return false;

      const paymentAmountNum = Number(paymentForm.amount);
      const hasAmount = !!paymentForm.amount && !isNaN(paymentAmountNum);
      const hasFile = !!paymentForm.file;
      if (hasFile && !hasAmount) {
        showWarning(tTitle("amount") + " 을(를) 입력해주세요.");
        return false;
      }
      if (hasAmount && !hasFile) {
        showWarning(tTitle("file") + " 자금청구서 파일 첨부가 필요합니다.");
        return false;
      }

      let createdTransportationId: number | undefined;

      await addCreate(
        { by: identity.company.type, companyId: identity.company.id },
        {
          name: values.name || "transportation",
          shippedFromList: values.shippedFromList,
          shippedTo: values.shippedTo!,
          shippedToAddress: values.shippedToAddress || "",
          shippedToRecipientName: values.shippedToRecipientName,
          shippedToContact: values.shippedToContact,
          forwarderInvoiceNumbers:
            values.forwarderInvoiceNumbers.filter(Boolean),
          houseBlNumbers: values.houseBlNumbers.filter(Boolean),
          mawbNumber: values.mawbNumber || null,
          domesticCarrierName: values.domesticCarrierName || null,
          domesticTrackingNumber: values.domesticTrackingNumber || null,
          flightNumber: null,
          flightDepartureAirport: null,
          flightArrivalAirport: null,
          flightEtd: null,
          flightEta: values.flightEta ? DateUtils.toDateTimeString(values.flightEta) : null,
          flightAtd: null,
          flightAta: null,
          finalEta: values.finalEta ?? null,
          cost: values.cost || 0,
          proformaInvoices: values.proformaInvoices.map((p) => ({ id: p.id })),
          files: values.files.filter(isFile),
        },
        (createdId: number) => {
          createdTransportationId = createdId;
        }
      );

      const paymentAmount = Number(paymentForm.amount);
      if (
        createdTransportationId &&
        paymentForm.amount &&
        !isNaN(paymentAmount)
      ) {
        try {
          let storedFile: { id: number; name: string } | null = null;
          if (paymentForm.file && isFile(paymentForm.file)) {
            const res = await fireStoreObject({}, { file: paymentForm.file });
            storedFile = { id: res.id, name: res.name };
          } else if (paymentForm.file && isStoredObject(paymentForm.file)) {
            storedFile = {
              id: (paymentForm.file as any).id,
              name: (paymentForm.file as any).name,
            };
          }
          const issuedOn = new Date(paymentForm.issuedOn);
          issuedOn.setHours(0, 0, 0, 0);

          await fireCreatePayment(
            {
              agencyId: identity.company.id,
              transportationId: createdTransportationId,
            },
            {
              issuedOn,
              file: storedFile,
              amount: { value: paymentAmount, currency: "KRW" as const },
              isRemitted: paymentForm.isRemitted,
              remittedOn: paymentForm.isRemitted
                ? paymentForm.remittedOn
                : null,
            }
          );
        } catch (e) {
          showError(e);
        }
      }

      onSuccess?.();
      handleClose();
      return true;
    },
    [identity?.company?.type, identity?.company?.id, paymentForm.amount, paymentForm.file, paymentForm.issuedOn, paymentForm.isRemitted, paymentForm.remittedOn, addCreate, onSuccess, handleClose, showWarning, tTitle, fireCreatePayment, fireStoreObject, showError]
  );

  const initialValues = useMemo(
    (): TransportationFormFields => ({
      id: -1,
      shippedFromList: shippedFromList ?? [],
      shippedTo: shippedTo,
      forwarderInvoiceNumbers: [""],
      houseBlNumbers: [""],
      details: [],
      files: [],
      proformaInvoices: proformaInvoices ?? [],
      shippedToRecipientName: defaultShippingInfo?.recipientName,
      shippedToContact: defaultShippingInfo?.contact,
      shippedToAddress: defaultShippingInfo
        ? defaultShippingInfo.address +
          (defaultShippingInfo.detailAddress
            ? ` ${defaultShippingInfo.detailAddress}`
            : "")
        : undefined,
    }),
    [shippedTo, shippedFromList, proformaInvoices, defaultShippingInfo]
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent
        borderRadius={"8px"}
        overflow={"hidden"}
        maxWidth={"1600px"}
      >
        <ModalBody padding={"0"}>
          <Formik<TransportationFormFields>
            enableReinitialize={true}
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
            validate={validate}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <Grid
                as={"form"}
                height={"100%"}
                width={"100%"}
                gridTemplateColumns={"1fr"}
                gridTemplateRows={"auto 1fr"}
              >
                <AppListHeader
                  headers={[
                    <Heading key={"header"} as={"h2"} fontSize={"14px"}>
                      {tTitle("upload_transportation")}
                    </Heading>,
                  ]}
                  filters={[]}
                  actions={[
                    <AppButton
                      key={"cancel"}
                      isDisabled={formik.isSubmitting}
                      onClick={handleClose}
                    >
                      {tTitle("cancel")}
                    </AppButton>,
                    <AppButton
                      key={"submit"}
                      variant={"primary"}
                      isLoading={formik.isSubmitting}
                      isDisabled={formik.isSubmitting}
                      onClick={() => formik.submitForm()}
                    >
                      {tTitle("upload")}
                    </AppButton>,
                  ]}
                  showNavigateToBack={false}
                />
                <TransportationForm
                  formik={formik}
                  isEditing={true}
                  hiddenFields={BASE_MODAL_HIDDEN_FIELDS}
                  rightFooter={
                    identity?.company?.type === "AGENCY" ? (
                      <Flex flexDirection={"column"} gap={"8px"}>
                        <Text
                          fontSize={"14px"}
                          fontWeight={"600"}
                          color={"#444440"}
                        >
                          {tTitle("transportation_payment")}
                        </Text>
                        <Flex
                          flexDirection={"column"}
                          gap={"12px"}
                          padding={"12px"}
                          border={"1px solid #E0E0DC"}
                          borderRadius={"6px"}
                        >
                          <TransportationPaymentInlineForm
                            value={paymentForm}
                            onChange={setPaymentForm}
                            isDisabled={formik.isSubmitting}
                            onBlNumberParsed={(blNumber) => {
                              const parsed = blNumber.trim();
                              if (!parsed) return;
                              const existing = (formik.values.houseBlNumbers ?? []).filter(
                                (b) => b && b.trim()
                              );
                              // 이미 같은 B/L 이 있으면 무시, 없으면 새로 추가
                              if (existing.some((b) => b.trim() === parsed)) {
                                return;
                              }
                              formik.setFieldValue("houseBlNumbers", [...existing, parsed]);
                            }}
                          />
                        </Flex>
                      </Flex>
                    ) : undefined
                  }
                />
              </Grid>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
